/**
 * <dm-thoughts> — customer thought bubbles (snippets/dm-trust-row.liquid).
 *
 * Each slot shows one thought at a time. A thought's CSS animation runs once, and its end starts
 * the next thought in that slot, so pausing the animations pauses everything with no timers.
 *
 * Slots: three rows on phones (the server's data-slot). From 750, two columns when the longest
 * lines fit the row, otherwise one.
 *
 * Nothing moves until the row is on screen. With reduced motion this file does nothing and the
 * server's first bubbles stand still. Hover, tapping the row or the keyboard-only pause button
 * hold it (A11Y-MOT-3).
 */

const BUBBLE_ANIMATIONS = new Set(['dm-thought-bubble', 'dm-thought-bubble-end']);
const PHONE = '(max-width: 749px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const PHONE_ROWS = 3;
// Seconds between the slots' first thoughts, so they read as different people.
const SLOT_STAGGER = 1.2;
// Seconds of empty space before the next thought in a slot starts forming.
const NEXT_THOUGHT_GAP = 0.36;

class DmThoughts extends HTMLElement {
  #thoughts = [];
  #queues = [];
  #layoutKey = '';
  #started = false;
  #inView = null;
  #resize = null;

  connectedCallback() {
    this.#thoughts = [...this.querySelectorAll('.dm-thought')];
    if (!this.#thoughts.length || window.matchMedia(REDUCED_MOTION).matches) return;

    this.addEventListener('animationend', this.#onAnimationEnd);
    this.addEventListener('click', this.#onClick);

    this.#inView = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-offscreen', !entry?.isIntersecting);
      if (entry?.isIntersecting && !this.#started) this.#start();
    });
    this.#inView.observe(this);

    this.#resize = new ResizeObserver(() => {
      if (this.#started) this.#start();
    });
    this.#resize.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('animationend', this.#onAnimationEnd);
    this.removeEventListener('click', this.#onClick);
    this.#inView?.disconnect();
    this.#resize?.disconnect();
    this.#started = false;
  }

  /** Lays out the slots and starts each one, unless a resize left the layout unchanged. */
  #start() {
    const key = this.#layout();
    if (this.#started && key === this.#layoutKey) return;
    this.#layoutKey = key;
    this.#started = true;
    this.setAttribute('data-playing', '');

    for (const thought of this.#thoughts) thought.classList.remove('is-on');
    this.#queues.forEach((queue, slot) => {
      if (queue[0]) this.#play(queue[0], slot * SLOT_STAGGER);
    });
  }

  /** @returns {string} what this layout is, to tell whether a resize changed it */
  #layout() {
    const thoughts = this.#thoughts;
    this.removeAttribute('data-columns');

    if (window.matchMedia(PHONE).matches) {
      this.#assign((_, index) => index % PHONE_ROWS);
      return 'rows';
    }

    this.#assign(() => 0);
    const list = this.querySelector('.dm-trust__thoughts');
    if (thoughts.length < 2 || !list) return 'one';

    // The wider half shares the first column, so the row needs the least width to hold both.
    const byWidth = [...thoughts].sort((a, b) => b.offsetWidth - a.offsetWidth);
    const wide = new Set(byWidth.slice(0, Math.ceil(byWidth.length / 2)));
    this.setAttribute('data-columns', '2');
    this.#assign((thought) => (wide.has(thought) ? 0 : 1));
    if (list.scrollWidth <= list.clientWidth) return 'two';

    this.removeAttribute('data-columns');
    this.#assign(() => 0);
    return 'one';
  }

  /** @param {(thought: HTMLElement, index: number) => number} slotOf */
  #assign(slotOf) {
    this.#queues = [];
    this.#thoughts.forEach((thought, index) => {
      const slot = slotOf(thought, index);
      thought.dataset.slot = String(slot);
      (this.#queues[slot] ??= []).push(thought);
    });
  }

  /**
   * @param {HTMLElement} thought
   * @param {number} delay - seconds before it starts forming
   */
  #play(thought, delay) {
    thought.style.setProperty('--dm-thought-delay', `${delay}s`);
    thought.classList.remove('is-on');
    // A slot with a single line replays the same element: restart its animations.
    void thought.offsetWidth;
    thought.classList.add('is-on');
  }

  /** @param {AnimationEvent} event */
  #onAnimationEnd = (event) => {
    if (!BUBBLE_ANIMATIONS.has(event.animationName)) return;
    const thought = /** @type {HTMLElement} */ (event.target).closest('.dm-thought');
    if (!(thought instanceof HTMLElement)) return;

    thought.classList.remove('is-on');
    const queue = this.#queues.find((slot) => slot?.includes(thought));
    const next = queue?.[(queue.indexOf(thought) + 1) % queue.length];
    if (next) this.#play(next, NEXT_THOUGHT_GAP);
  };

  #onClick = () => {
    const paused = this.toggleAttribute('data-paused');
    this.querySelector('.dm-trust__pause')?.setAttribute('aria-pressed', String(paused));
  };
}

if (!customElements.get('dm-thoughts')) {
  customElements.define('dm-thoughts', DmThoughts);
}
